<template>
  <div class="roaming-rates ml-14 pl-4 mt-20">
    <!-- Popular Searches -->
    <div v-if="popularSearches.length" class="mb-12">
      <h3 class="font-semibold text-[18px] leading-[27px] font-[700] mb-4">
        Popular Searches
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          v-for="country in popularSearches"
          :key="country.id"
          @click="$emit('select-country', country)"
          class="text-primary hover:underline font-semibold text-[18px] leading-[27px] font-[600] text-left"
        >
          {{ country.displayName }}
        </button>
      </div>
    </div>

    <!-- Destinations -->
    <div class="destinations">
      <h3 class="font-semibold text-[18px] leading-[27px] font-[700] mb-6">
        Destinations
      </h3>

      <!-- Alphabet Navigation -->
      <div class="w-full border-b border-gray-200 mb-2">
        <div class="flex justify-between">
          <button
            v-for="range in alphabetRanges"
            :key="range"
            class="w-[150px] px-3 py-2 text-font-primary font-semibold text-[18px] leading-[27px] font-[700]"
            :class="
              currentRange === range
                ? 'border-b-4 border-primary -mb-[2px]'
                : 'text-gray-600'
            "
            @click="currentRange = range"
          >
            {{ range }}
          </button>
        </div>
      </div>

      <!-- Countries List -->
      <div v-if="currentRange" class="country-groups">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div v-for="letter in currentLetters" :key="letter">
            <h4 class="text-lg font-bold mb-4">{{ letter }}</h4>
            <div class="flex flex-col">
              <button
                v-for="country in countriesByLetter[letter]"
                :key="country.id"
                @click="$emit('select-country', country)"
                class="text-primary font-semibold text-[18px] leading-[27px] font-[700] hover:underline text-left"
              >
                {{ country.displayName }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

// Props
const props = defineProps({
  tariffData: {
    type: Object,
    required: true,
  },
});

// Emits
defineEmits(["select-country"]);

// State
const currentRange = ref("A-D");

// Computed
const popularSearches = computed(() => {
  if (!props.tariffData?.popularSearches) return [];
  return [...props.tariffData.popularSearches].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
});

const alphabetRanges = ["A-D", "E-H", "I-L", "M-P", "Q-T", "U-Z"];

const currentLetters = computed(() => {
  if (!currentRange.value) return [];
  const [start, end] = currentRange.value.split("-");
  const letters = [];
  for (let i = start.charCodeAt(0); i <= end.charCodeAt(0); i++) {
    letters.push(String.fromCharCode(i));
  }
  return letters;
});

const allCountries = computed(() => {
  if (!props.tariffData?.zones) return [];
  return props.tariffData.zones.reduce((acc, zone) => {
    return [...acc, ...zone.countries];
  }, []);
});

const countriesByLetter = computed(() => {
  const grouped = {};

  allCountries.value.forEach((country) => {
    const firstLetter = country.displayName[0].toUpperCase();
    if (!grouped[firstLetter]) {
      grouped[firstLetter] = [];
    }
    grouped[firstLetter].push(country);
  });

  // Sort countries within each letter
  Object.keys(grouped).forEach((letter) => {
    grouped[letter].sort((a, b) => a.displayName.localeCompare(b.displayName));
  });

  return grouped;
});
</script>

<style scoped>
.roaming-rates {
  max-width: 1430px;
}
</style>
